import { useCallback } from 'react';
import { z } from 'zod';
import { queryKeys, sessionStore, useApiQuery } from '@zagru/platform';
import type { QueryOpts } from './common';

/*
 * W2 (spec §4, ADR-0502): `GET /api/v1/platform/enablement` — what is switched on for the tenant. Any member of the
 * tenant may read it: it says which modules, packs and apps are on, never data. The web gates every surface on it
 * (nav, routes, palette, help, notification preferences, terminology overlays); the server guards the endpoints
 * (`404 app.not_installed`).
 */

/*
 * What decides that a surface exists. A surface — a nav entry, a route, a command, a help page,
 * a notification preference — is visible when the viewer's permission holds **and** its enablement expression holds.
 *
 * The key language (the same on the server, see `enablement-contract`):
 *   `module:<id>`         the module is switched on for the tenant (`platform.modules.<id>.enabled`)
 *   `pack:<id>`           the pack is installed; family ids `regulatory-tr|eu|uk`, `country-base` included
 *   `app:<id>`            the app is INSTALLED (an app still in SETUP does not show its surfaces)
 *   `app:category:<cat>`  some INSTALLED app has that category (`marketplace`, `parcel`, `einvoice`, `telematics` …)
 *   `country:<ISO>`       the tenant operates in that country — for recommendations, never alone to show a surface
 *
 * `requires` needs every key it names; `requiresAny` needs at least one. With both, both must hold.
 */

/** One key of the enablement language (`module:fleet`, `app:tr.uetds`, `app:category:parcel` …). */
export type EnablementKey = `module:${string}` | `pack:${string}` | `app:${string}` | `country:${string}`;

/** The enablement expression a surface declares. An empty or absent expression means "core": always enabled. */
export interface Requirement {
  /** Every key must hold. */
  requires?: EnablementKey | EnablementKey[];
  /** At least one key must hold. */
  requiresAny?: EnablementKey[];
}

/** True when the expression names at least one key (i.e. the surface is not core). */
export function hasRequirement(expr: Requirement | undefined | null): expr is Requirement {
  return !!expr && (requiredKeys(expr).length > 0 || (expr.requiresAny?.length ?? 0) > 0);
}

/** The `requires` keys as a list. */
export function requiredKeys(expr: Requirement): EnablementKey[] {
  return expr.requires === undefined ? [] : Array.isArray(expr.requires) ? expr.requires : [expr.requires];
}

export const appInstallationStatuses = ['INSTALLED', 'SETUP'] as const;
export type AppInstallationStatus = (typeof appInstallationStatuses)[number];

export const enablementSchema = z.object({
  /** Module ids switched on (`fleet`, `customs`, `yard`, `multimodal`, `crm`, `bonded`, `oms`, `vas`, `ai`, `screening`). */
  modules: z.array(z.string()).default([]),
  /** Installed pack ids plus the family ids (`regulatory-tr`, `regulatory-eu`, `regulatory-uk`, `country-base`). */
  packs: z.array(z.string()).default([]),
  /** Installation rows that are INSTALLED or SETUP (pack apps appear here too, app id = pack id). */
  apps: z.array(z.object({ id: z.string(), status: z.string() })).default([]),
  /** Distinct categories of the INSTALLED apps (`marketplace`, `parcel`, `einvoice`, `telematics` …). */
  categories: z.array(z.string()).default([]),
  /** The tenant's operating countries (ISO alpha-2) — recommendations only. */
  countries: z.array(z.string()).default([]),
  /** Opaque version (etag) of this answer. */
  version: z.string().nullish(),
});
export type Enablement = z.infer<typeof enablementSchema>;

export const enablementKeys = {
  all: queryKeys.custom('platform', 'enablement'),
  /** Per tenant: a workspace switch reads the other tenant's answer (the switch also clears the cache). */
  current: (tenantId: string | undefined) => queryKeys.custom('platform', 'enablement', tenantId ?? ''),
};

/**
 * The tenant's enablement. Fresh for a minute and re-read when the window regains focus: an app installed in another
 * tab (or by another administrator) shows its surfaces on the next focus, as the contract asks — there is no push.
 * Install and uninstall commands invalidate `enablementKeys.all` themselves.
 */
export function useEnablement(options?: QueryOpts<Enablement>) {
  const tenantId = sessionStore((s) => s.tenant?.id);
  const signedIn = sessionStore((s) => !!s.user);
  return useApiQuery<Enablement>(enablementKeys.current(tenantId), (client, signal) => client.get('/api/v1/platform/enablement', { schema: enablementSchema, signal }), {
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled: signedIn,
    ...(options ?? {}),
  });
}

const APP_CATEGORY = 'app:category:';

/** Whether one key of the language holds. An unknown key form never holds. */
export function isKeyEnabled(enablement: Enablement, key: EnablementKey | string): boolean {
  if (key.startsWith(APP_CATEGORY)) return enablement.categories.includes(key.slice(APP_CATEGORY.length));
  const colon = key.indexOf(':');
  if (colon < 0) return false;
  const kind = key.slice(0, colon);
  const id = key.slice(colon + 1);
  switch (kind) {
    case 'module':
      return enablement.modules.includes(id);
    case 'pack':
      return enablement.packs.includes(id);
    case 'app':
      // SETUP counts as not enabled for surfaces: the App Center shows the app as "Kurulumda" until its wizard ends.
      return enablement.apps.some((a) => a.id === id && a.status === 'INSTALLED');
    case 'country':
      return enablement.countries.some((c) => c.toUpperCase() === id.toUpperCase());
    default:
      return false;
  }
}

/** The pure evaluator: every `requires` key holds and, when `requiresAny` is given, at least one of its keys. */
export function isEnabled(enablement: Enablement, expr: Requirement | undefined | null): boolean {
  if (!hasRequirement(expr)) return true;
  if (!requiredKeys(expr).every((key) => isKeyEnabled(enablement, key))) return false;
  const any = expr.requiresAny ?? [];
  return any.length === 0 || any.some((key) => isKeyEnabled(enablement, key));
}

export interface RequiresResult {
  /** The surface may be shown. */
  enabled: boolean;
  /** Enablement has not been read yet: the surface is not decided (a route shows its skeleton, not "not enabled"). */
  pending: boolean;
}

/*
 * Failure policy: when the enablement read FAILS, every surface counts as enabled (fail open). Enablement decides
 * what the interface offers, not what a tenant may do — the server still answers `404 app.not_installed` for an app
 * that is not installed, and permissions still apply. A transient outage of this one endpoint must never make the
 * product look empty or tell somebody their feature is "not installed" when it is.
 */

/** One surface's decision: `{ enabled, pending }`. A core surface (no expression) is enabled at once. */
export function useRequires(expr: Requirement | undefined | null): RequiresResult {
  const query = useEnablement();
  if (!hasRequirement(expr)) return { enabled: true, pending: false };
  if (query.data) return { enabled: isEnabled(query.data, expr), pending: false };
  if (query.isError) return { enabled: true, pending: false };
  // Not read at all (no signed-in session to read it for — a public page, a test harness): nothing to decide against.
  if (query.fetchStatus === 'idle') return { enabled: true, pending: false };
  return { enabled: false, pending: true };
}

/**
 * A stable `(expr) => boolean` for filtering lists (nav, palette commands, settings items, help pages). While the read
 * is pending it answers `true` — the same choice the nav makes for feature flags — so an existing tenant's menu does
 * not blink items away and back on every load; the routes behind them wait for the answer themselves
 * (`RequireEnablement`). On error it fails open, as above.
 */
export function useEnablementCheck(): (expr: Requirement | undefined | null) => boolean {
  const { data } = useEnablement();
  return useCallback((expr: Requirement | undefined | null) => (data ? isEnabled(data, expr) : true), [data]);
}
